import { FC } from "react";
import { CustomTypography } from "../CustomTypography/CustomTypography";
import { useTranslation } from "react-i18next";
import { colors } from "../../styles/colors";
import { CustomButtonOutlined } from "../CustomButtonOutlined/CustomButtonOutlined";
import useStyles from "./styles";
import { useParams } from "react-router";
import { useGetContratByVehicleId } from "../../api/contrat";
import { getCurrentFleetId } from "../../utils/getCurrentFleet";
import { CustomIconButton } from "../CustomIconButton/CustomIconButton";
import { useGetOneVehicle } from "../../api/vehicule";
import { translateVehicleStatus } from "../../utils/types";
import dayjs from "dayjs";

type props = {
  onClickEditIcon?: () => void;
  /** See CustomVehicleInfos: needed wherever there is no :id route segment. */
  vehicleId?: number;
};

export const CustomVehicleContrat: FC<props> = ({
  onClickEditIcon,
  vehicleId: vehicleIdProp,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { id = "" } = useParams();
  const vehicleId = vehicleIdProp ?? parseInt(id);
  const { contrats } = useGetContratByVehicleId(getCurrentFleetId(), vehicleId);
  const { vehicule } = useGetOneVehicle(getCurrentFleetId(), vehicleId);

  /**
   * When a vehicle has no rent_contract row this card used to render the bare
   * field LABELS ("Durée", "Date d'entrée | Date de sortie", "Loyer HT") as if
   * they were data. Nothing distinguished "no contract" from "contract with
   * blank values", so the sheet looked complete - especially next to a
   * DOCUMENTS card reading CONTRAT / Téléchargé, which is an uploaded PDF and
   * NOT a rent contract. Assigning the vehicle then failed with "No contract
   * attach to the vehicle" and the message looked like a bug.
   *
   * Show the absence explicitly instead.
   */
  const contrat = contrats?.[0];
  const hasContract = !!contrat?.id;

  return (
    <div className={classes.subContainerContrat}>
      <div className={classes.buttonEdit}>
        <CustomIconButton
          onClick={onClickEditIcon}
          iconName={"pencil"}
          widthIcon={"20px"}
          heightIcon={"20px"}
        />
      </div>

      <div className={classes.flexParent}>
        <div className={classes.containerContrat}>
          <CustomTypography
            variant="h5"
            isUppercase
            text={t("panel.vehicule.contract", "Rental contract")}
            color={colors.primary}
          />

          {!hasContract ? (
            <div>
              <CustomTypography
                variant="link"
                text={t(
                  "panel.vehicule.noContract",
                  "No rent contract for this vehicle"
                )}
              />
              <CustomTypography
                variant="link"
                text={t(
                  "panel.vehicule.noContractHint",
                  "The signed contract PDF in the Documents card is not a rental contract. Add one to be able to assign this vehicle."
                )}
              />
            </div>
          ) : (
          <div>
            {contrats?.[0]?.duration?.toString() ? (
              <CustomTypography
                variant="link"
                text={`${contrats?.[0]?.duration?.toString()} mois`}
              />
            ) : (
              <CustomTypography
                variant="link"
                text={t("panel.vehicule.duration", "Duration")}
              />
            )}

            {contrats?.[0]?.start_date ? (
              <CustomTypography
                variant="link"
                text={`${
                  contrats?.[0]?.start_date
                    ? dayjs(contrats[0].start_date).format("DD-MM-YYYY")
                    : ""
                } |&nbsp;
                    ${
                      contrats?.[0]?.end_date
                        ? dayjs(contrats[0].end_date).format("DD-MM-YYYY")
                        : ""
                    }`}
              />
            ) : (
              <CustomTypography
                variant="link"
                text={`${t(
                  "panel.vehicule.dateOfEntry",
                  "Date of entry"
                )} |&nbsp;
                    ${t("panel.vehicule.releaseDate", "Release date")}`}
              />
            )}

            {contrats?.[0]?.rental_tax ? (
              <CustomTypography
                variant="link"
                text={`${t(
                  "panel.vehicule.rentExcludingTax",
                  "Rent exluding tax"
                )} ${contrats?.[0]?.rental_tax} €`}
              />
            ) : (
              <CustomTypography
                variant="link"
                text={t("panel.vehicule.rentExcludingTax", "Rent exluding tax")}
              />
            )}
          </div>
          )}
        </div>
        <div>
          <div style={{ marginBottom: "14px" }}>
            <CustomTypography
              isUppercase
              variant="h5"
              text={t("panel.vehicule.statusDone", "Status")}
              color={colors.primary}
            />
          </div>

          <CustomButtonOutlined
            variant="success"
            label={translateVehicleStatus(t, vehicule?.fleetVehicleStatut)}
            width={"fit-content"}
            padding={"0px 10px"}
          />
        </div>
      </div>
    </div>
  );
};
